// /src/pages/auth/InstallAppPage.jsx

import { useNavigate, useLocation } from 'react-router-dom';
import { Smartphone, Share, MoreVertical, Plus, Download, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui';

export function InstallAppPage() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get first name / email from navigation state if available
  const firstName = location.state?.firstName || '';
  const email = location.state?.email || '';

  const handleContinue = () => {
    navigate('/login', {
      state: {
        signupSuccess: true,
        message: 'Account created successfully! Please sign in.'
      },
      replace: true
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-8">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="bg-white py-8 px-6 shadow-md rounded-lg">
          {/* Logo */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <img
                src="/images/logo.png"
                alt="ACL Care"
                className="h-16 w-auto"
              />
            </div>
          </div>

          {/* Welcome Message */}
          <div className="text-center mb-8">
            <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Smartphone className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {firstName ? `Welcome, ${firstName}!` : 'Account Created!'}
            </h1>
            <p className="text-gray-600">
              For the best experience, install ACL Care Pro on your phone's home screen.
            </p>
            {email && (
              <p className="text-sm text-gray-500 mt-2">
                We also emailed these instructions to <strong className="text-gray-700">{email}</strong>.
              </p>
            )}
          </div>

          {/* Installation Instructions */}
          <div className="space-y-4">
            {/* iPhone Instructions */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">📱</span>
                <h3 className="font-semibold text-gray-900">iPhone (Safari)</h3>
              </div>
              <ol className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="font-medium text-gray-900 shrink-0">1.</span>
                  <span>Open <strong className="text-gray-900">app.aclcarepro.com</strong> in Safari</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-medium text-gray-900 shrink-0">2.</span>
                  <span className="flex items-center gap-1">
                    Tap the <strong className="text-gray-900">Share</strong> button
                    <Share className="h-4 w-4 text-gray-500" />
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-medium text-gray-900 shrink-0">3.</span>
                  <span className="flex items-center gap-1">
                    Scroll down and tap <strong className="text-gray-900">"Add to Home Screen"</strong>
                    <Plus className="h-4 w-4 text-gray-500" />
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-medium text-gray-900 shrink-0">4.</span>
                  <span>Tap <strong className="text-gray-900">"Add"</strong> in the top right</span>
                </li>
              </ol>
            </div>

            {/* Android Instructions */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">🤖</span>
                <h3 className="font-semibold text-gray-900">Android (Chrome)</h3>
              </div>
              <ol className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="font-medium text-gray-900 shrink-0">1.</span>
                  <span>Open <strong className="text-gray-900">app.aclcarepro.com</strong> in Chrome</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-medium text-gray-900 shrink-0">2.</span>
                  <span className="flex items-center gap-1">
                    Tap the <strong className="text-gray-900">menu</strong> (three dots)
                    <MoreVertical className="h-4 w-4 text-gray-500" />
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-medium text-gray-900 shrink-0">3.</span>
                  <span className="flex items-center gap-1">
                    Tap <strong className="text-gray-900">"Add to Home Screen"</strong> or <strong className="text-gray-900">"Install App"</strong>
                    <Download className="h-4 w-4 text-gray-500" />
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-medium text-gray-900 shrink-0">4.</span>
                  <span>Tap <strong className="text-gray-900">"Add"</strong> or <strong className="text-gray-900">"Install"</strong></span>
                </li>
              </ol>
            </div>
          </div>

          {/* Continue Button */}
          <div className="mt-8 space-y-3">
            <Button onClick={handleContinue} fullWidth>
              Continue to Sign In
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
            
            <p className="text-center text-xs text-gray-500">
              You can always install the app later from your browser
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}